/**
 * PURPOSE: Grupo compacto "Descargas" del detalle de una prueba de Atletismo: planilla PDF (descarga + vista previa),
 *   en pista los archivos FinishLynx de la prueba (lynx_<N°>.evt / .sch) y, en una combinada o en sus pruebas, el
 *   "Reporte combinada (PDF)" (clasificación tras N pruebas; en la combinada es la única descarga).
 * RESPONSIBILITIES: Pedir cada archivo al backend en el momento (datos frescos de la base, sin caché), guardarlo con el
 *   nombre de Content-Disposition, mostrar el estado de carga y los errores con toast. La vista previa reutiliza el visor
 *   de la planilla FEDUP (FTR-0058). La fase (`series` | `final`) es la de la serie abierta en el panel.
 * COLLABORATORS: PruebaHeader.jsx (lo muestra junto a "Oficializar"), PruebaDetailPanel.jsx (fase y N°),
 *   services/athleticsResultsService.js, FullScreenPlanilla/shared/FedupPreviewModal.jsx.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/SheetDownloads.jsx
 */
import React, { useCallback, useState } from "react";
import { toast } from "react-hot-toast";
import { FaFilePdf } from "react-icons/fa6";
import { FiDownload, FiEye } from "react-icons/fi";

import FedupPreviewModal from "../../../components/FullScreenModal/FullScreenPlanilla/shared/FedupPreviewModal.jsx";
import {
  downloadAthleticsCombinedReport,
  downloadAthleticsPlanilla,
  downloadAthleticsPruebaLynx,
  saveAthleticsFile,
} from "../../../services/athleticsResultsService.js";
import { parseAthleticsError } from "../../../utils/athleticsResultsErrors.js";
import { Spinner } from "./ui.jsx";

const TRACK_FORMATS = new Set(["CPT", "MAR", "PST"]);
const FIELD_FORMATS = new Set(["SHL", "SVA"]);

const BUTTON =
  "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-bold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50";
const LIGHT = "border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50";
const STRONG = "border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-700";

export const sheetPhaseOf = (round) => (round?.type === "final" ? "final" : "series");

const SheetDownloads = ({ idevent, idsport, prueba, round }) => {
  const [busy, setBusy] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const phase = sheetPhaseOf(round);
  const isFinal = phase === "final";
  const isTrack = TRACK_FORMATS.has(prueba?.format);
  const scheduleNo = round?.schedule?.scheduleNo ?? null;
  const pruebaId = prueba?.id;
  const isCombined = prueba?.format === "PCB";
  const inCombined = isCombined || Boolean(prueba?.parentId);

  const loadPdf = useCallback(
    async (_id, { signal } = {}) => (await downloadAthleticsPlanilla({ idevent, idsport, pruebaId, phase, signal })).blob,
    [idevent, idsport, pruebaId, phase]
  );

  if (!prueba) return null;
  const hasSheet = Boolean(round) && (isTrack || FIELD_FORMATS.has(prueba.format));
  if (!hasSheet && !inCombined) return null;

  const run = async (kind, task) => {
    setBusy(kind);
    try {
      const file = await task();
      saveAthleticsFile(file);
      toast.success(`Descargado: ${file.filename}`);
    } catch (error) {
      toast.error(parseAthleticsError(error).message);
    } finally {
      setBusy(null);
    }
  };

  const combinedButton = inCombined ? (
    <button
      type="button"
      onClick={() => run("combined", () => downloadAthleticsCombinedReport({ idevent, idsport, pruebaId }))}
      disabled={Boolean(busy)}
      title="Descargar la clasificación de la combinada con los puntos de las pruebas disputadas hasta ahora"
      className={`${BUTTON} ${LIGHT}`}
    >
      {busy === "combined" ? <Spinner /> : <FaFilePdf aria-hidden="true" />} Reporte combinada (PDF)
    </button>
  ) : null;

  if (!hasSheet) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1.5" aria-label="Descargas">
        <span className="px-1 text-[11px] font-black uppercase tracking-wide text-slate-500">Descargas</span>
        {combinedButton}
      </div>
    );
  }

  const lynx = (kind) =>
    run(kind, () => downloadAthleticsPruebaLynx({ idevent, idsport, pruebaId, phase, kind }));
  const lynxTitle = (kind) =>
    `Descarga lynx_${scheduleNo ?? "N"}.${kind} (${isFinal ? "final" : "todas las series"} de esta prueba). ` +
    `Renombrar a lynx.evt/lynx.sch para FinishLynx.`;

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 rounded-xl border px-2 py-1.5 ${
        isFinal ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"
      }`}
      aria-label="Descargas"
    >
      <span className="px-1 text-[11px] font-black uppercase tracking-wide text-slate-500">
        Descargas{scheduleNo ? ` · N° ${scheduleNo}` : ""}
        {isFinal ? <span className="ml-1 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] text-white">FINAL</span> : null}
      </span>
      <button
        type="button"
        onClick={() => run("pdf", () => downloadAthleticsPlanilla({ idevent, idsport, pruebaId, phase }))}
        disabled={Boolean(busy)}
        title={`Descargar la planilla PDF (${isFinal ? "final" : "series"}) con los datos actuales`}
        className={`${BUTTON} ${isFinal ? STRONG : LIGHT}`}
      >
        {busy === "pdf" ? <Spinner /> : <FaFilePdf aria-hidden="true" />} Planilla
      </button>
      <button
        type="button"
        onClick={() => setPreviewOpen(true)}
        disabled={Boolean(busy)}
        title="Vista previa de la planilla PDF"
        aria-label="Vista previa de la planilla"
        className={`${BUTTON} ${LIGHT}`}
      >
        <FiEye aria-hidden="true" /> Vista
      </button>
      {isTrack ? (
        <>
          <button type="button" onClick={() => lynx("evt")} disabled={Boolean(busy)} title={lynxTitle("evt")} className={`${BUTTON} ${LIGHT}`}>
            {busy === "evt" ? <Spinner /> : <FiDownload aria-hidden="true" />} .evt
          </button>
          <button type="button" onClick={() => lynx("sch")} disabled={Boolean(busy)} title={lynxTitle("sch")} className={`${BUTTON} ${LIGHT}`}>
            {busy === "sch" ? <Spinner /> : <FiDownload aria-hidden="true" />} .sch
          </button>
        </>
      ) : null}
      {combinedButton}
      <FedupPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        matchId={pruebaId}
        loadPdf={loadPdf}
        sport={isTrack ? "atletismo" : "atletismoCampo"}
      />
    </div>
  );
};

export default SheetDownloads;
